/**
 * Bambora Payment Provider
 * Handles Bambora checkout integration with customer form approach
 */

import { PaymentProvider } from './base.js';
import { getCurrencyCode } from '../../core/i18n.js';

export class BamboraProvider extends PaymentProvider {
    constructor(config, system) {
        super(config, system);
        this.providerType = 'bambora';
    }

    async processOrder(order) {
        // Note: This method is now deprecated for Bambora
        // Bambora checkout should use the customer form approach in Checkout_Template.html
        // This is kept for backward compatibility but will redirect to form-based checkout
        
        console.warn('BamboraProvider.processOrder is deprecated. Use customer form approach instead.');
        
        // Redirect to checkout page with customer form
        const checkoutUrl = this.config.checkoutUrl || '/checkout/';
        window.location.href = checkoutUrl;
        
        return {
            success: true,
            message: 'Redirecting to customer form checkout',
            checkoutUrl: checkoutUrl
        };
    }

    async confirmOrder(orderId) {
        // Orders are already created via callback - use standard confirmation
        const url = `${this.config.confirmApiUrl}?merchantId=${this.config.merchantId}&orderId=${orderId}&provider=bambora`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept-Language': this.config.locale,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Bambora confirmation error! status: ${response.status}`);
        }

        return await response.json();
    }

    renderCheckout(data) {
        // Bambora uses redirect-based checkout with customer form
        console.log('BamboraProvider: Using customer form checkout approach');
        
        // If we somehow end up here, show a message directing to the form
        const checkoutContainer = document.getElementById('bambora-checkout') || 
                                document.getElementById('checkout-container');
                                
        if (checkoutContainer) {
            checkoutContainer.innerHTML = `
                <div class="bambora-redirect-info" style="padding: 20px; background: #e3f2fd; border: 1px solid #2196f3; border-radius: 4px; margin: 20px 0;">
                    <h3 style="color: #1976d2; margin-top: 0;">🔄 Bambora Checkout</h3>
                    <p style="color: #1976d2; margin: 10px 0;">
                        Bambora checkout uses a customer form approach. 
                        Please fill in your details in the billing form to proceed with payment.
                    </p>
                    <p style="color: #1976d2; margin: 10px 0; font-size: 14px;">
                        If you're seeing this message, the checkout flow will be handled by the customer form.
                    </p>
                </div>
            `;
        }
    }

    /**
     * Helper method for the new API approach (used by customer form)
     * @param {Object} orderData - Order data from customer form
     * @returns {Promise<Object>} API response
     */
    static async initiatePayment(orderData) {
        const response = await fetch('/cart/api/v1/payments/initiate/bambora', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Bambora API error! status: ${response.status} - ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Build order data for Bambora API
     * @param {Object} customerFormData - Customer form data
     * @param {Array} cartItems - Cart items
     * @param {Object} config - Configuration
     * @returns {Object} Order data for API
     */
    static buildOrderData(customerFormData, cartItems, config) {
        // NOTE: Total amount is NOT calculated in JavaScript
        // Backend calculates the correct total from OrderRows, handling B2C/B2B VAT correctly
        
        // Build customer object according to CustomerModel structure
        const customer = {
            name: customerFormData.name || '',
            lastname: customerFormData.lastname || '',
            companyName: customerFormData.companyName || null,
            address: customerFormData.address || '',
            zip: customerFormData.zip || '',
            city: customerFormData.city || '',
            country: customerFormData.country || 'SE',
            email: customerFormData.email || '',
            phone: customerFormData.phone || '',
            type: customerFormData.companyName ? 1 : 0, // 1 = Company, 0 = Private
            organizationRegistrationId: customerFormData.organizationRegistrationId || null,
            vatId: null
        };

        // Build order rows according to OrderRowModel structure
        const orderRows = cartItems.map(item => {
            // Identity (GUIDs are translation-proof), quantity, and for gift cards the chosen
            // amount via giftCardData. Type and TaxRate are intentionally NOT sent - the server
            // derives them from the database.
            const orderRow = {
                productNr: item.ProductNr,
                productName: item.ProductName,
                productPrice: parseFloat(item.ProductPrice),
                orderCount: item.OrderCount,
                orderUnit: 'st',
                rowSum: parseFloat(item.ProductPrice) * item.OrderCount,
                productUrl: item.ProductUrl || '',
                imageUrl: item.ImageUrl || ''
            };

            // Forward the translation-proof identifiers when present
            if (item.ProductId) {
                orderRow.productId = item.ProductId;
            }
            if (item.ProductVariantId) {
                orderRow.productVariantId = item.ProductVariantId;
            }
            
            // CRITICAL: Add GiftCardData if this is a gift card
            // This ensures gift card information is preserved through the payment flow
            if (item.isGiftCard && item.giftCardData) {
                orderRow.giftCardData = item.giftCardData;
                console.log('Added GiftCardData to order row for product:', item.ProductNr, 'Data:', item.giftCardData);
            }
            
            return orderRow;
        });

        // Build delivery address if different delivery address is selected
        let deliveryAddress = null;
        if (customerFormData.differentDeliveryAddress) {
            deliveryAddress = {
                name: customerFormData.deliveryName || '',
                lastname: customerFormData.deliveryLastname || '',
                companyName: customerFormData.deliveryCompany || null,
                address: customerFormData.deliveryAddress || '',
                zip: customerFormData.deliveryZip || '',
                city: customerFormData.deliveryCity || '',
                country: 'SE',
                email: customerFormData.deliveryEmail || customer.email,
                phone: customerFormData.deliveryPhone || customer.phone
            };
        }

        // Get discount data from DiscountManager if available (NEW MULTI-DISCOUNT APPROACH)
        let discounts = [];
        
        // Try to get discount data from global ecommerce system
        if (window.ecommerce && window.ecommerce.checkout && window.ecommerce.checkout.discountManager) {
            const discountManager = window.ecommerce.checkout.discountManager;
            if (discountManager.discountStack && discountManager.discountStack.length > 0) {
                discounts = discountManager.discountStack.map(discount => ({
                    code: discount.code,
                    amount: Math.round(discount.discountAmount * 100), // Convert to öre
                    description: discount.description || discount.name || `Rabatt: ${discount.code}`
                }));
                console.log('BamboraProvider: Including multiple discounts in order:', discounts);
            }
        }

        // Get gift card payments from the GiftCardPaymentManager (Phase 3, max 1 card, B6)
        const giftCardPayments =
            window.ecommerce?.checkout?.giftCardPaymentManager?.getGiftCardPayments?.() || [];

        // Get currency from i18n system or config
        const currency = getCurrencyCode() || config.purchaseCurrency || 'SEK';
        const locale = config.locale || 'sv-SE';
        const purchaseCountry = config.purchaseCountry || 'se';

        // Create order data according to InitiatePaymentRequest structure
        // NOTE: No 'amount' field - backend calculates from OrderRows
        return {
            merchantId: config.merchantId || '00000000-0000-0000-0000-000000000001',
            currency: currency,
            orderReference: `${Date.now()}`,
            returnUrl: this.absoluteUrl(config.confirmUrl, '/thank-you/'),
            cancelUrl: this.absoluteUrl(config.checkoutUrl, '/checkout/'),
            locale: locale,
            purchaseCountry: purchaseCountry,
            customer: customer,
            orderRows: orderRows,
            deliveryAddress: deliveryAddress,
            sendReceiptToDeliveryAddress: customerFormData.sendReceiptToDeliveryAddress === 'on',
            termsUrl: config.termsUrl ? this.absoluteUrl(config.termsUrl) : null,
            // Include discount data if available (NEW MULTI-DISCOUNT APPROACH)
            discounts: discounts,
            // Include gift card payments (Phase 3) - empty array when none applied
            giftCardPayments: giftCardPayments,
            // CRITICAL FIX: Include ShippingData so PaymentsController can create shipping OrderRow
            // Use capital S to match InitiatePaymentRequest.ShippingData property
            ShippingData: customerFormData.shippingData
        };
    }
}
